<template lang="pug">
v-sheet.relative(
  height="90vh"
  width="100%"
)
  carousel-images(
    v-model="activeCarouselImage"
    :items="images"
  )
    template(#default="{ item }")
      parallax-image-wrapper(
        height="100%"
        :src="item"
      )

  .absolute.full-width.full-height.top-0.left-0.z-index-1
    v-overlay(
      v-if="mainImage"
      absolute
      :color="mainImageOverlay.overlay"
      :z-index="-1"
      opacity="1"
    )
    .relative.d-flex.flex-column.full-width.full-height.py-4
      .flex-grow-1.d-flex.flex-column.gap-20.justify-center.align-center
        event-name-editor(
          v-model="eventName"
          :is-editable="isEditable"
          :default-name="event.name"
          :loading="loading.name"
          :styledColor="eventName.length ? mainImageOverlay.text || '' : ''"
          :max-name-length="140"
          @update="updateEventName"
        )
        .mb-1.weight-400(
          :style="`color: ${mainImageOverlay.text || ''};`"
          :class=`{
            'redesignDisabledText--text user-select-none': !eventName.length,
            'title-1': $vuetify.breakpoint.xs,
            'headline': $vuetify.breakpoint.sm,
            'display-1': $vuetify.breakpoint.mdAndUp,
          }`
        ) {{ event.level.name }}

        .d-flex.flex-wrap.gap-16.justify-center(v-if="!isEditable && userSubscribeButtons.length")
          component(
            v-for="(btn, idx) in userSubscribeButtons"
            :key="idx"
            :is="btn.is"
            v-bind="btn.attrs"
            v-on="btn.listeners"
          )

      .absolute.bottom-0.full-width.mb-16.pb-16.d-flex.justify-center(v-if="images.length > 1")
        app-pagination.mb-5(
          v-model="activePaginationCarouselImageIdx"
          :length="images.length"
          total-visible="none"
        )

  event-requests-dialog(
    v-model="requests.dialog"
    :event="requests.target"
  )
</template>

<script>
import ParallaxImageWrapper from '@/components/ParallaxImageWrapper.vue';
import AppButton from '@/components/user-redesign/controls/AppButton.vue';
import AppPagination from '@/components/user-redesign/controls/AppPagination.vue';
import AppEditableContent, { BUTTONS_TYPES } from '@/components/user-redesign/AppEditableContent.vue';
import mixinTime from '@/mixins/mixinTime';
import EventRequestsDialog from '@/modules/UserEvents/components/MemberEvents/EventRequestsDialog.vue';
import rules from '@/helpers/rules';
import landingMainPlaceholder from '@/assets/landing-main-placeholder.jpg';
import CarouselImages from '@/modules/UserEventLanding/components/event-landing/main/CarouselImages.vue';
import EventNameEditor from '@/modules/UserEventLanding/components/event-landing/main/EventNameEditor.vue';
import TeamsRequestsDialog from '@/modules/Team/components/EventTeamRegister/TeamsRequestsDialog.vue';
import { deepObjectMerge } from '@/modules/UserEventLanding/functions';

const MAX_NAME_LENGTH = 256;

export default {
  name: 'MainContainer',

  components: {
    CarouselImages,
    AppEditableContent,
    ParallaxImageWrapper,
    AppButton,
    AppPagination,
    EventRequestsDialog,
    EventNameEditor,
  },

  mixins: [mixinTime],

  props: {
    event: {
      type: Object,
      required: true,
    },
    isEditable: {
      type: Boolean,
      required: false,
    },
  },

  data() {
    return {
      rules,

      requests: {
        dialog: false,
        target: {},
      },

      eventName: this.event.name,
      MAX_NAME_LENGTH,

      BUTTONS_TYPES,

      activeCarouselImage: 0,

      loading: {
        name: false,
        level: false,
      },
    };
  },

  computed: {
    images() {
      const filteredImages = this.event.images.filter((image) => !image.is_main).map((image) => image.url || image.thumb);
      return [this.mainImage || landingMainPlaceholder, ...filteredImages].filter(Boolean);
    },
    mainImage() {
      const { main_image } = this.event || {};
      return main_image?.url || main_image?.thumb || '';
    },
    activePaginationCarouselImageIdx: {
      get() {
        return this.activeCarouselImage + 1;
      },
      set(value) {
        this.activeCarouselImage = value - 1;
      },
    },
    mainImageOverlay() {
      return this.event.options.frontend_params.mainImageOverlayColor;
    },
    userSubscribeButtons() {
      const { event, $vuetify, $user } = this;
      const { smAndDown, xs } = $vuetify.breakpoint;
      const baseAttrs = {
        class: ['rounded-12'],
        height: smAndDown ? '40px' : '48px',
        'min-width': xs ? '85%' : '350px',
      };
      const baseButton = {
        is: AppButton,
        visible: true,
        attrs: baseAttrs,
        listeners: {},
      };

      const { isMember, isTrainer } = $user;
      const registerButton = deepObjectMerge(baseButton, {
        visible: isMember && !event.request_status,
        attrs: {
          label: event.registerTitle,
          // disabled: !(event.options.isRegisterPeriod || event.isHighest) || event.isClosed,
          disabled: event.isClosed,
        },
        listeners: {
          click: () => this.$router.push({ name: 'event-register-redesign' }),
        },
      });

      return [
        deepObjectMerge(baseButton, {
          visible: isMember && event.request_status && event.isActive,
          attrs: {
            outlined: true,
            label: 'Управление заявками',
            to: { name: 'event-applications-redesign', params: { id: event.id } },
          },
        }),
        deepObjectMerge(baseButton, {
          visible: isMember && event.request_status,
          attrs: {
            label: event.registerTitle,
            disabled: !event.options.isRegisterPeriod || event.isClosed,
            to: { name: 'event-register-redesign', params: { id: event.id } },
          },
        }),
        registerButton,
        {
          is: TeamsRequestsDialog,
          visible: isTrainer,
          attrs: deepObjectMerge(baseAttrs, {
            outlined: true,
            text: false,
            color: 'redesignPrimary',
            class: ['redesignPrimaryDarken--text'],
            label: 'Управление заявками',
            eventId: this.event.id,
          }),
        },
        deepObjectMerge(registerButton, {
          visible: isTrainer && !event.request_status,
          listeners: {
            click: () => this.$router.push({ name: 'event-team-register', params: { id: event.id } }),
          },
        }),
      ].filter((btn) => btn.visible);
    },
  },

  methods: {
    async updateEventName() {
      this.loading.name = true;
      const params = {
        name: this.eventName,
      };
      await this.$rApi.events.updateEvent({ args: [this.event.id, params] });
      this.$toast.success('Название изменено');
      this.event.name = this.eventName;
      this.loading.name = false;
    },
    showRequestsDialog() {
      this.requests.target = this.event;
      this.requests.dialog = true;
    },
  },
};
</script>
